/**
 * The model rows the Edit Dashboard dialog reads (`t_hub_bim_model`).
 *
 * The model is the HUB's now, so there is exactly one row — and this module is
 * still the one place that normalises it for a surface. It answers three things:
 *
 *   · the row's FACTS — name, byte count, the revision time — read from the
 *     `GET /api/bim-models` payload the dialog already fetches;
 *   · WHICH row the hub serves. A re-serve REPLACES the row, so the newest one is
 *     the served one — the same rule the store and the layout use
 *     (`newestBimModelRow`, `utils/bimModelRefresh.ts`), which is why the three
 *     surfaces cannot disagree about the active model;
 *   · the FILE NAME a download should carry. The row's `name` is whatever the
 *     operator typed into the upload form and the file route
 *     (`…/bim-models/:modelId/file`) carries no extension at all, so the stored
 *     object's own key — `bim-model/<owner>/<format>/<uuid>.<format>`, the layout
 *     the hub's upload route writes — is what states the format.
 *
 * Total by contract: a payload missing a field (an older hub, a row with no
 * stored object) reads as a row the dialog can still list, and never throws —
 * a throw inside a computed is a blank screen that lint, types and the unit
 * suite all walk past.
 *
 * Pure on purpose: no Vue, no `apiFetch`, no DOM — `bimDashboardModels.test.ts`
 * drives the normaliser and the file-name rule on the payloads the hub sends.
 */

import { bimModelFormatFromName, type BimModelFormat } from './bimModelFormat';
import { bimModelIdOf, newestBimModelRow } from './bimModelRefresh';
import { field, wireNumber, wireString } from './wire';

/** One row of a dashboard's own model list, as the dialog shows it. */
export interface DashboardBimModel {
  /** The row's id — also the id the file route and the delete route take. */
  id: string;
  name: string;
  /** The stored object's size in bytes (`0` when the hub sent none). */
  bytes: number;
  /** The revision time (`updatedAt`, else `createdAt`), or `null`. */
  updatedAt: string | null;
  /** The stored object's own key, when the hub sent one (never shown raw). */
  s3Key: string;
  /** The kind of file the row holds, when its key or name says. */
  format: BimModelFormat | null;
  /** The row this dashboard renders (the newest one — see the module header). */
  active: boolean;
}

/** One row, normalised — `null` when it names no id (nothing can be fetched or deleted for it). */
function parseRow(raw: unknown): Omit<DashboardBimModel, 'active'> | null {
  const id = bimModelIdOf(raw);
  if (id === null) return null;
  const name = wireString(field(raw, 'name')) ?? '';
  const s3Key = wireString(field(raw, 's3Key')) ?? '';
  return {
    id,
    name: name.length > 0 ? name : id,
    bytes: wireNumber(field(raw, 'fileSize')) ?? 0,
    updatedAt: wireString(field(raw, 'updatedAt')) ?? wireString(field(raw, 'createdAt')) ?? null,
    s3Key,
    format: bimModelFormatFromName(s3Key) ?? bimModelFormatFromName(name),
  };
}

/**
 * The dashboard's model rows in the hub's own order, each marked with whether
 * it is the one the dashboard renders. An answer that is not an array, or holds
 * no row this module can name, reads as an empty list (the dialog's empty
 * state) rather than as an error.
 */
export function dashboardBimModels(rows: unknown): DashboardBimModel[] {
  const list = Array.isArray(rows) ? rows : [];
  const parsed = list.map(parseRow).filter((row): row is Omit<DashboardBimModel, 'active'> => row !== null);
  const activeId = bimModelIdOf(newestBimModelRow(list));
  // A single row is the active one by definition — and it is still the one
  // `newestBimModelRow` picks, so the two rules cannot drift.
  return parsed.map((row) => ({ ...row, active: activeId === null ? parsed.length === 1 : row.id === activeId }));
}

/** Characters no file system in this product takes, replaced in a download name. */
const UNSAFE_NAME_CHARS = /[\\/:*?"<>|]/g;

/**
 * The file name a download of this row should carry: the row's own name with
 * the stored format's extension — the route serves bytes without a name, and a
 * manager that saves `TorikDemoHouse-v2.5-site` with no extension cannot be
 * opened by anything.
 *
 * A name that already ends in the model's extension is left alone; one whose
 * format nothing states (an old row, a key this build does not know) keeps its
 * own name rather than growing a guess; a nameless row is `model` + the format.
 */
export function bimModelDownloadName(name: unknown, s3Key: unknown): string {
  const raw = (typeof name === 'string' ? name.trim() : '').replace(UNSAFE_NAME_CHARS, '-');
  const format = bimModelFormatFromName(s3Key) ?? bimModelFormatFromName(raw);
  const base = raw.length > 0 ? raw : 'model';
  if (format === null) return base;
  return bimModelFormatFromName(base) === format ? base : `${base}.${format}`;
}
